import React, { useState } from 'react';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CalendarDaysIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

const PERIODOS_RAPIDOS = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'ayer', label: 'Ayer' },
  { id: 'semana', label: 'Últimos 7 días' },
  { id: 'mes', label: 'Este mes' },
];

export function CallHistoryFilterBar({
  periodo,
  fechaDesde,
  fechaHasta,
  estadoFiltro,
  orden,
  direccion,
  searchTerm,
  setSearchTerm,
  cambiarPeriodo,
  cambiarRangoFechas,
  cambiarEstadoFiltro,
  cambiarOrden,
  refetch,
  loading,
}) {
  const [showDatePicker, setShowDatePicker] = useState(periodo === 'rango');
  const [tempDesde, setTempDesde] = useState(fechaDesde || '');
  const [tempHasta, setTempHasta] = useState(fechaHasta || '');

  const handleSortChange = (e) => {
    const val = e.target.value;
    switch (val) {
      case 'fecha_desc':
        cambiarOrden('fecha', 'DESC');
        break;
      case 'fecha_asc':
        cambiarOrden('fecha', 'ASC');
        break;
      case 'duracion_desc':
        cambiarOrden('duracion', 'DESC');
        break;
      case 'duracion_asc':
        cambiarOrden('duracion', 'ASC');
        break;
      default:
        cambiarOrden('fecha', 'DESC');
    }
  };

  const handleAplicarRango = (e) => {
    e.preventDefault();
    if (!tempDesde) return;
    cambiarRangoFechas(tempDesde, tempHasta || tempDesde);
  };

  const currentSortVal = `${orden}_${(direccion || 'DESC').toLowerCase()}`;

  return (
    <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-xs mb-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Lado Izquierdo: Períodos rápidos y rango personalizado */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Botones de Período en Barra Agrupada */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl">
            {PERIODOS_RAPIDOS.map((p) => {
              const isActive = periodo === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setShowDatePicker(false);
                    cambiarPeriodo(p.id);
                  }}
                  disabled={loading}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-xs font-bold'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}

            {/* Botón para alternar selector de fechas */}
            <button
              type="button"
              onClick={() => setShowDatePicker((prev) => !prev)}
              disabled={loading}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1 cursor-pointer ${
                periodo === 'rango' || showDatePicker
                  ? 'bg-indigo-600 text-white shadow-xs font-bold'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
              }`}
              title="Seleccionar rango de fechas específico"
            >
              <CalendarDaysIcon className="w-3.5 h-3.5" />
              <span>Rango</span>
            </button>
          </div>
        </div>

        {/* Lado Derecho: Búsqueda, Filtro Estado, Ordenamiento y Refrescar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Campo de Búsqueda Rápida */}
          <div className="relative w-full sm:w-64">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar cliente, teléfono, gestor..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-8 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5 rounded cursor-pointer"
                title="Limpiar búsqueda"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filtro por Estado */}
          <div className="relative">
            <select
              value={estadoFiltro}
              onChange={(e) => cambiarEstadoFiltro(e.target.value)}
              disabled={loading}
              className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl px-3 py-1.5 pr-7 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 cursor-pointer"
              title="Filtrar por estado de la llamada"
            >
              <option value="todos">Todos los estados</option>
              <option value="Contestada">🟢 Contestada</option>
              <option value="No contestó">🔴 No contestó</option>
              <option value="Ocupado">🟡 Ocupado</option>
              <option value="Falló">⚪ Falló</option>
            </select>
          </div>

          {/* Dropdown de Ordenamiento */}
          <div className="relative">
            <select
              value={currentSortVal}
              onChange={handleSortChange}
              disabled={loading}
              className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl px-3 py-1.5 pr-7 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 cursor-pointer"
              title="Ordenar llamadas"
            >
              <option value="fecha_desc">🕒 Más recientes</option>
              <option value="fecha_asc">🕒 Más antiguas</option>
              <option value="duracion_desc">⏱️ Mayor duración</option>
              <option value="duracion_asc">⏱️ Menor duración</option>
            </select>
          </div>

          {/* Botón Refrescar */}
          <button
            type="button"
            onClick={refetch}
            disabled={loading}
            className="p-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
            title="Actualizar datos"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-600' : ''}`} />
          </button>
        </div>
      </div>

      {/* Sub-barra colapsable para Rango de Fechas Personalizado */}
      {showDatePicker && (
        <form
          onSubmit={handleAplicarRango}
          className="pt-2 border-t border-slate-100 flex flex-wrap items-center gap-2.5 animate-in fade-in duration-150"
        >
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
            <FunnelIcon className="w-3.5 h-3.5 text-indigo-600" />
            Rango específico:
          </span>

          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">
            <label className="text-[10px] font-bold text-slate-500 uppercase">Desde:</label>
            <input
              type="date"
              value={tempDesde}
              onChange={(e) => setTempDesde(e.target.value)}
              className="bg-transparent text-xs font-semibold text-slate-800 focus:outline-hidden cursor-pointer"
            />
          </div>

          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">
            <label className="text-[10px] font-bold text-slate-500 uppercase">Hasta:</label>
            <input
              type="date"
              value={tempHasta}
              onChange={(e) => setTempHasta(e.target.value)}
              className="bg-transparent text-xs font-semibold text-slate-800 focus:outline-hidden cursor-pointer"
            />
          </div>

          <button
            type="submit"
            disabled={!tempDesde || loading}
            className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow-xs transition cursor-pointer"
          >
            Aplicar Filtro
          </button>

          {periodo === 'rango' && (
            <button
              type="button"
              onClick={() => {
                setShowDatePicker(false);
                cambiarPeriodo('hoy');
              }}
              className="px-2 py-1 text-slate-500 hover:text-slate-800 text-xs font-semibold transition cursor-pointer"
            >
              Restablecer a Hoy
            </button>
          )}
        </form>
      )}
    </div>
  );
}
